import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { User, Building2, Mail, Phone, Globe, Lock, Copy, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

export default function PartnerSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [partner, setPartner] = useState(null);
  
  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchPartnerData();
  }, []);

  const fetchPartnerData = async () => {
    try {
      const token = localStorage.getItem('partner_token');
      const response = await axios.get(`${API_URL}/api/partner/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartner(response.data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/login?ref=${partner?.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      const token = localStorage.getItem('partner_token');
      await axios.post(`${API_URL}/api/partner/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your partner account settings</p>
      </div>

      <div className="space-y-6">
        {/* Profile Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Company Information
            </CardTitle>
            <CardDescription>Your registered partner details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground text-sm">Company Name</Label>
                <p className="font-medium">{partner?.company_name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Contact Name</Label>
                <p className="font-medium">{partner?.contact_name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Email</Label>
                <p className="font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  {partner?.email}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Phone</Label>
                <p className="font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  {partner?.phone || 'Not provided'}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Website</Label>
                <p className="font-medium flex items-center gap-2">
                  <Globe className="w-4 h-4 text-muted-foreground" />
                  {partner?.website || 'Not provided'}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Business Type</Label>
                <p className="font-medium capitalize">{partner?.business_type || 'Not specified'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referral Information */}
        <Card>
          <CardHeader>
            <CardTitle>Referral Program</CardTitle>
            <CardDescription>Share your referral link to bring in new customers</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground text-sm">Your Referral Code</Label>
              <p className="font-mono text-lg font-bold text-primary">{partner?.referral_code}</p>
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Referral Link</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input 
                  value={`${window.location.origin}/login?ref=${partner?.referral_code}`}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button variant="outline" size="icon" onClick={copyReferralLink}>
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Billing Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Billing Settings
            </CardTitle>
            <CardDescription>Manage where invoices and billing notifications are sent</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="billing-email">Billing Email</Label>
              <p className="text-sm text-muted-foreground mb-2">
                Invoices and billing notifications will be sent to this email
              </p>
              <div className="flex items-center gap-2">
                <Input
                  id="billing-email"
                  type="email"
                  value={partner?.billing_email || partner?.email || ''}
                  onChange={(e) => setPartner({...partner, billing_email: e.target.value})}
                  placeholder="billing@yourcompany.com"
                />
                <Button 
                  variant="outline" 
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('partner_token');
                      await axios.put(`${API_URL}/api/partner/profile`, {
                        billing_email: partner?.billing_email
                      }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success('Billing email updated');
                    } catch (error) {
                      toast.error('Failed to update billing email');
                    }
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Change Password
            </CardTitle>
            <CardDescription>Update your account password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label htmlFor="current-password">Current Password</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                />
              </div>
              <Button type="submit" disabled={changingPassword || !currentPassword || !newPassword}>
                {changingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  'Change Password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
